import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../../api/client";
import { useTabParam } from "../../hooks/useTabParam";
import type {
  AttendanceSummaryResponse,
  DailyReportResponse,
  DailyReportRow,
  Site,
  WeeklyReportResponse,
} from "../../types";
import {
  attendanceStatusTone,
  formatAttendanceStatus,
} from "../../utils/formatters";

const REPORTS_TABS = ["daily", "weekly", "attendance", "builder"] as const;
type Tab = (typeof REPORTS_TABS)[number];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function mondayOf(dateIso: string): string {
  const d = new Date(dateIso);
  const day = d.getDay(); // 0 = Sun
  const diff = (day + 6) % 7; // back to Monday
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}

export default function ReportsPage() {
  const [tab, setTab] = useTabParam(REPORTS_TABS, "daily");
  const [sites, setSites] = useState<Site[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        setSites(await api.get<Site[]>("/api/sites"));
      } catch {
        setSites([]);
      }
    })();
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-500">
          Attendance, hours worked, and overtime exports.
        </p>
      </div>

      <nav className="flex gap-1 border-b border-gray-200">
        <TabButton current={tab} value="daily" onClick={setTab}>
          Daily Report
        </TabButton>
        <TabButton current={tab} value="weekly" onClick={setTab}>
          Weekly Report
        </TabButton>
        <TabButton current={tab} value="attendance" onClick={setTab}>
          Attendance
        </TabButton>
        <TabButton current={tab} value="builder" onClick={setTab}>
          Report Builder
        </TabButton>
      </nav>

      {tab === "daily" && <DailyTab sites={sites} />}
      {tab === "weekly" && <WeeklyTab sites={sites} />}
      {tab === "attendance" && <AttendanceTab sites={sites} />}
      {tab === "builder" && <ReportBuilderTab />}
    </div>
  );
}

function TabButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab;
  value: Tab;
  onClick: (t: Tab) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={[
        "px-4 py-2 text-sm font-semibold",
        active
          ? "border-b-2 border-pme-blue text-pme-blue"
          : "text-gray-500 hover:text-gray-800",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function SiteFilter({
  sites,
  value,
  onChange,
}: {
  sites: Site[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
    >
      <option value="">All sites</option>
      {sites.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

function statusBadge(row: DailyReportRow): { label: string; class: string } {
  return {
    label: formatAttendanceStatus(row.status),
    class: attendanceStatusTone(row.status),
  };
}

function exportUrl(
  type: "daily" | "weekly",
  dateIso: string,
  siteId: string,
): string {
  const params = new URLSearchParams({ type, date: dateIso });
  if (siteId) params.set("site_id", siteId);
  return `/api/reports/export/csv?${params.toString()}`;
}

// ---------- Daily ----------

function DailyTab({ sites }: { sites: Site[] }) {
  const [date, setDate] = useState<string>(todayIso());
  const [siteId, setSiteId] = useState<string>("");
  const [data, setData] = useState<DailyReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ date });
      if (siteId) params.set("site_id", siteId);
      const res = await api.get<DailyReportResponse>(
        `/api/reports/daily?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError((err as Error).message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [date, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="ml-2 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <SiteFilter sites={sites} value={siteId} onChange={setSiteId} />
        <a
          href={exportUrl("daily", date, siteId)}
          className="ml-auto rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          Export CSV
        </a>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">First In</th>
              <th className="px-4 py-3">Last Out</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3 text-right">Breaks</th>
              <th className="px-4 py-3 text-right">Break Time</th>
              <th className="px-4 py-3 text-right">Overtime</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && data?.rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  No employees match the current filters.
                </td>
              </tr>
            )}
            {!loading &&
              data?.rows.map((row, i) => {
                const badge = statusBadge(row);
                const overage = row.total_break_overage_minutes;
                const breakTimeClass =
                  overage > 15
                    ? "text-red-700 font-semibold"
                    : overage > 0
                      ? "text-amber-700"
                      : "";
                const rowClass =
                  row.status === "absent"
                    ? "bg-red-50/40"
                    : row.is_late
                      ? "bg-amber-50/40"
                      : i % 2
                        ? "bg-gray-50/40"
                        : "";
                return (
                  <tr key={row.employee_id} className={rowClass}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">
                        {row.employee_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {row.employee_number}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {row.first_clock_in
                        ? new Date(row.first_clock_in).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                      {row.is_late && (
                        <span className="ml-2 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800">
                          LATE
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {row.last_clock_out
                        ? new Date(row.last_clock_out).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.total_hours_worked_display}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-xs">
                      {row.breaks_taken}/{row.breaks_allowed}
                    </td>
                    <td
                      className={[
                        "px-4 py-3 text-right tabular-nums text-xs",
                        breakTimeClass,
                      ].join(" ")}
                    >
                      {row.total_break_duration_display}
                      {row.total_allowed_break_minutes > 0 && (
                        <>
                          {" / "}
                          {row.total_allowed_break_display}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.total_overtime_display}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          "rounded-full px-2 py-0.5 text-xs font-semibold",
                          badge.class,
                        ].join(" ")}
                      >
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Weekly ----------

function WeeklyTab({ sites }: { sites: Site[] }) {
  const [weekStart, setWeekStart] = useState<string>(mondayOf(todayIso()));
  const [siteId, setSiteId] = useState<string>("");
  const [data, setData] = useState<WeeklyReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ week_start: weekStart });
      if (siteId) params.set("site_id", siteId);
      const res = await api.get<WeeklyReportResponse>(
        `/api/reports/weekly?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError((err as Error).message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [weekStart, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const weekLabel = useMemo(() => {
    if (!data) return "";
    return `${data.week_start} → ${data.week_end}`;
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">
          Week starting (Mon)
          <input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(mondayOf(e.target.value))}
            className="ml-2 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <SiteFilter sites={sites} value={siteId} onChange={setSiteId} />
        <a
          href={exportUrl("weekly", weekStart, siteId)}
          className="ml-auto rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          Export CSV
        </a>
      </div>

      {weekLabel && <p className="text-xs text-gray-500">{weekLabel}</p>}
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3 text-right">Days Present</th>
              <th className="px-4 py-3 text-right">Days Late</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3 text-right">Breaks</th>
              <th className="px-4 py-3 text-right">Break Time</th>
              <th className="px-4 py-3 text-right">Overtime</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && data?.rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No data for this week.
                </td>
              </tr>
            )}
            {!loading &&
              data?.rows.map((row, i) => {
                const overage = row.total_break_overage_minutes;
                const breakClass =
                  overage > 15
                    ? "text-red-700 font-semibold"
                    : overage > 0
                      ? "text-amber-700"
                      : "";
                return (
                <tr
                  key={row.employee_id}
                  className={
                    row.days_late > 0
                      ? "bg-amber-50/40"
                      : i % 2
                        ? "bg-gray-50/40"
                        : ""
                  }
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">
                      {row.employee_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {row.employee_number}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {row.days_present}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {row.days_late}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {row.total_hours_worked_display}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-xs">
                    {row.breaks_taken}/{row.breaks_allowed}
                  </td>
                  <td
                    className={[
                      "px-4 py-3 text-right tabular-nums text-xs",
                      breakClass,
                    ].join(" ")}
                  >
                    {row.total_break_duration_display}
                    {row.total_allowed_break_minutes > 0 && (
                      <>
                        {" / "}
                        {row.total_allowed_break_display}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {row.total_overtime_display}
                  </td>
                </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Attendance ----------

function AttendanceTab({ sites }: { sites: Site[] }) {
  const [date, setDate] = useState<string>(todayIso());
  const [siteId, setSiteId] = useState<string>("");
  const [data, setData] = useState<AttendanceSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams({ date });
      if (siteId) params.set("site_id", siteId);
      setData(
        await api.get<AttendanceSummaryResponse>(
          `/api/reports/attendance-summary?${params.toString()}`,
        ),
      );
    } catch (err) {
      setError((err as Error).message || "Failed to load");
    }
  }, [date, siteId]);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(id);
  }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="ml-2 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <SiteFilter sites={sites} value={siteId} onChange={setSiteId} />
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total" value={data?.total_employees ?? "—"} tone="gray" />
        <Stat label="Present" value={data?.present ?? "—"} tone="green" />
        <Stat label="Absent" value={data?.absent ?? "—"} tone="red" />
        <Stat
          label="Late arrivals"
          value={data?.late_arrivals ?? "—"}
          tone="amber"
        />
        <Stat label="On Shift" value={data?.on_shift ?? "—"} tone="green" />
        <Stat label="On Break" value={data?.on_break ?? "—"} tone="amber" />
        <Stat label="Completed" value={data?.completed ?? "—"} tone="gray" />
      </div>

      <section className="rounded-xl border border-gray-200 bg-white">
        <header className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Absent today</h2>
        </header>
        <ul className="divide-y divide-gray-100 text-sm">
          {(data?.absent_employees ?? []).length === 0 && (
            <li className="px-4 py-6 text-center text-gray-400">
              Nobody is absent today.
            </li>
          )}
          {data?.absent_employees.map((e) => (
            <li key={e.id} className="flex items-center justify-between px-4 py-3">
              <span className="font-medium text-gray-900">{e.name}</span>
              <span className="text-xs text-gray-500">{e.employee_number}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: "green" | "amber" | "red" | "gray";
}) {
  const toneClass = {
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-800",
    red: "bg-red-50 text-red-700",
    gray: "bg-gray-50 text-gray-700",
  }[tone];
  return (
    <div className={["rounded-xl p-4", toneClass].join(" ")}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-3xl font-semibold">{value}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Report Builder Tab
 * ──────────────────────────────────────────────────────────────────────────── */

interface AvailableField {
  code: string;
  name: string;
  source: string;
  data_type: string;
  category: string;
}

interface ColumnDef {
  field_code: string;
  display_name: string;
  aggregate: string | null;
}

interface FilterDef {
  field_code: string;
  operator: string;
  value: string;
}

interface SortDef {
  field_code: string;
  direction: "asc" | "desc";
}

interface SavedTemplate {
  id: number;
  name: string;
  description: string | null;
  is_shared: boolean;
  columns: ColumnDef[];
  filters: FilterDef[];
  group_by: string[] | null;
  sort_by: SortDef[] | null;
  created_by_id: number;
}

const OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not equals" },
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does not contain" },
  { value: "greater_than", label: "Greater than" },
  { value: "less_than", label: "Less than" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
];

const AGGREGATES = [
  { value: "", label: "None" },
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
];

function ReportBuilderTab() {
  const [availableFields, setAvailableFields] = useState<AvailableField[]>([]);
  const [columns, setColumns] = useState<ColumnDef[]>([]);
  const [filters, setFilters] = useState<FilterDef[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortDef[]>([]);
  const [results, setResults] = useState<Record<string, unknown>[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [templates, setTemplates] = useState<SavedTemplate[]>([]);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [saveShared, setSaveShared] = useState(false);
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [fields, tmpls] = await Promise.all([
          api.get<AvailableField[]>("/api/reports/builder/fields"),
          api.get<SavedTemplate[]>("/api/reports/builder/templates"),
        ]);
        setAvailableFields(fields);
        setTemplates(tmpls);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addColumn = (field: AvailableField) => {
    if (columns.some((c) => c.field_code === field.code)) return;
    setColumns([...columns, { field_code: field.code, display_name: field.name, aggregate: null }]);
  };

  const removeColumn = (idx: number) => setColumns(columns.filter((_, i) => i !== idx));

  const updateColumn = (idx: number, updates: Partial<ColumnDef>) =>
    setColumns(columns.map((c, i) => (i === idx ? { ...c, ...updates } : c)));

  const moveColumn = (idx: number, dir: -1 | 1) => {
    const next = [...columns];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setColumns(next);
  };

  const addFilter = () =>
    setFilters([...filters, { field_code: availableFields[0]?.code ?? "", operator: "equals", value: "" }]);

  const removeFilter = (idx: number) => setFilters(filters.filter((_, i) => i !== idx));

  const updateFilter = (idx: number, updates: Partial<FilterDef>) =>
    setFilters(filters.map((f, i) => (i === idx ? { ...f, ...updates } : f)));

  const runReport = async () => {
    if (columns.length === 0) { setError("Select at least one column."); return; }
    setRunning(true); setError(null); setResults(null);
    try {
      const body = {
        columns: columns.map((c) => ({ field_code: c.field_code, display_name: c.display_name, aggregate: c.aggregate || null })),
        filters: filters.filter((f) => f.field_code),
        group_by: groupBy.length > 0 ? groupBy : null,
        sort_by: sortBy.length > 0 ? sortBy : null,
        page: 1,
        per_page: 50,
      };
      const res = await api.post<{ data: Record<string, unknown>[]; total: number }>("/api/reports/builder/execute", body);
      setResults(res.data);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to run report");
    } finally {
      setRunning(false);
    }
  };

  const exportCsv = async () => {
    if (columns.length === 0) return;
    try {
      const body = {
        columns: columns.map((c) => ({ field_code: c.field_code, display_name: c.display_name, aggregate: c.aggregate || null })),
        filters: filters.filter((f) => f.field_code),
        group_by: groupBy.length > 0 ? groupBy : null,
        sort_by: sortBy.length > 0 ? sortBy : null,
      };
      const blob = await api.post<Blob>("/api/reports/builder/export", body);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed");
    }
  };

  const saveTemplate = async () => {
    if (!saveName.trim()) return;
    setError(null); setNotice(null);
    try {
      await api.post("/api/reports/builder/templates", {
        name: saveName,
        description: saveDesc || null,
        columns,
        filters,
        group_by: groupBy.length > 0 ? groupBy : null,
        sort_by: sortBy.length > 0 ? sortBy : null,
        is_shared: saveShared,
      });
      setNotice("Report template saved.");
      setShowSave(false);
      setSaveName(""); setSaveDesc(""); setSaveShared(false);
      const tmpls = await api.get<SavedTemplate[]>("/api/reports/builder/templates");
      setTemplates(tmpls);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save");
    }
  };

  const loadTemplate = (t: SavedTemplate) => {
    setColumns(t.columns);
    setFilters(t.filters);
    setGroupBy(t.group_by ?? []);
    setSortBy(t.sort_by ?? []);
    setResults(null);
    setNotice(`Loaded template: ${t.name}`);
  };

  const deleteTemplate = async (id: number) => {
    if (!confirm("Delete this report template?")) return;
    try {
      await api.delete(`/api/reports/builder/templates/${id}`);
      setTemplates(templates.filter((t) => t.id !== id));
      setNotice("Template deleted.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed");
    }
  };

  if (loading) return <p className="text-sm text-gray-400">Loading...</p>;

  const fieldsByCategory = availableFields.reduce<Record<string, AvailableField[]>>((acc, f) => {
    (acc[f.category] ??= []).push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {notice && <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</p>}

      <div className="flex gap-6">
        {/* Available fields */}
        <div className="w-[35%] shrink-0 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Available Fields</p>
          <div className="max-h-[500px] overflow-y-auto space-y-3 pr-1">
            {Object.entries(fieldsByCategory).map(([cat, flds]) => (
              <div key={cat}>
                <p className="mb-1 text-[11px] font-semibold uppercase text-gray-400">{cat}</p>
                <div className="space-y-0.5">
                  {flds.map((f) => (
                    <button
                      key={f.code}
                      type="button"
                      onClick={() => addColumn(f)}
                      disabled={columns.some((c) => c.field_code === f.code)}
                      className="w-full rounded px-2 py-1 text-left text-xs text-gray-700 hover:bg-blue-50 disabled:opacity-40"
                    >
                      {f.name}
                      <span className="ml-1 text-[10px] text-gray-400">{f.data_type}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Builder */}
        <div className="min-w-0 flex-1 space-y-5">
          {/* Selected columns */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Selected Columns ({columns.length})
            </p>
            {columns.length === 0 ? (
              <p className="text-sm text-gray-400">Click fields on the left to add columns.</p>
            ) : (
              <div className="space-y-1">
                {columns.map((col, i) => (
                  <div key={col.field_code} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm">
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveColumn(i, -1)} className="text-xs text-gray-400 hover:text-gray-700" disabled={i === 0}>&#9650;</button>
                      <button type="button" onClick={() => moveColumn(i, 1)} className="text-xs text-gray-400 hover:text-gray-700" disabled={i === columns.length - 1}>&#9660;</button>
                    </div>
                    <input
                      type="text"
                      value={col.display_name}
                      onChange={(e) => updateColumn(i, { display_name: e.target.value })}
                      className="flex-1 border-0 bg-transparent px-1 py-0 text-sm outline-none"
                    />
                    {groupBy.length > 0 && (
                      <select
                        value={col.aggregate ?? ""}
                        onChange={(e) => updateColumn(i, { aggregate: e.target.value || null })}
                        className="rounded border border-gray-200 px-1 py-0.5 text-xs"
                      >
                        {AGGREGATES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                    )}
                    <button type="button" onClick={() => removeColumn(i)} className="text-xs text-red-500 hover:text-red-700">&times;</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filters */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Filters</p>
              <button type="button" onClick={addFilter} className="text-xs text-pme-blue hover:underline">
                Add Filter
              </button>
            </div>
            {filters.map((f, i) => (
              <div key={i} className="mb-1 flex items-center gap-2">
                <select value={f.field_code} onChange={(e) => updateFilter(i, { field_code: e.target.value })} className="rounded border border-gray-300 px-2 py-1 text-xs">
                  {availableFields.map((af) => <option key={af.code} value={af.code}>{af.name}</option>)}
                </select>
                <select value={f.operator} onChange={(e) => updateFilter(i, { operator: e.target.value })} className="rounded border border-gray-300 px-2 py-1 text-xs">
                  {OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                </select>
                {!["is_empty", "is_not_empty"].includes(f.operator) && (
                  <input type="text" value={f.value} onChange={(e) => updateFilter(i, { value: e.target.value })} className="rounded border border-gray-300 px-2 py-1 text-xs" placeholder="Value" />
                )}
                <button type="button" onClick={() => removeFilter(i)} className="text-xs text-red-500">&times;</button>
              </div>
            ))}
          </div>

          {/* Group by */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Group By</p>
            <div className="flex flex-wrap gap-2">
              {availableFields.filter((f) => ["text", "boolean"].includes(f.data_type)).slice(0, 20).map((f) => (
                <label key={f.code} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={groupBy.includes(f.code)}
                    onChange={() => setGroupBy((prev) => prev.includes(f.code) ? prev.filter((x) => x !== f.code) : [...prev, f.code])}
                    className="h-3 w-3 rounded border-gray-300 text-pme-blue"
                  />
                  {f.name}
                </label>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Sort By</p>
            <div className="flex items-center gap-2">
              <select
                value={sortBy[0]?.field_code ?? ""}
                onChange={(e) => setSortBy(e.target.value ? [{ field_code: e.target.value, direction: sortBy[0]?.direction ?? "asc" }] : [])}
                className="rounded border border-gray-300 px-2 py-1 text-xs"
              >
                <option value="">None</option>
                {columns.map((c) => <option key={c.field_code} value={c.field_code}>{c.display_name}</option>)}
              </select>
              {sortBy.length > 0 && (
                <select
                  value={sortBy[0]?.direction}
                  onChange={(e) => setSortBy([{ ...sortBy[0], direction: e.target.value as "asc" | "desc" }])}
                  className="rounded border border-gray-300 px-2 py-1 text-xs"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => void runReport()} disabled={running || columns.length === 0} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {running ? "Running..." : "Run Report"}
            </button>
            <button type="button" onClick={() => void exportCsv()} disabled={columns.length === 0} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Export CSV
            </button>
            <button type="button" onClick={() => setShowSave(!showSave)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Save Template
            </button>
          </div>

          {/* Save modal */}
          {showSave && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
              <input type="text" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Report name" className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
              <input type="text" value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)} placeholder="Description (optional)" className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={saveShared} onChange={(e) => setSaveShared(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-pme-blue" />
                Share with others
              </label>
              <button type="button" onClick={() => void saveTemplate()} disabled={!saveName.trim()} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                Save
              </button>
            </div>
          )}

          {/* Results table */}
          {results && (
            <div>
              <p className="mb-2 text-xs text-gray-500">
                Showing {results.length} of {total} results
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase text-gray-500">
                      {columns.map((c) => (
                        <th key={c.field_code} className="px-3 py-2 whitespace-nowrap">{c.display_name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, ri) => (
                      <tr key={ri} className="border-b border-gray-100 hover:bg-gray-50">
                        {columns.map((c) => (
                          <td key={c.field_code} className="px-3 py-2 whitespace-nowrap text-gray-700">
                            {String(row[c.field_code] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Saved templates */}
      {templates.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Saved Reports</p>
          <div className="space-y-1">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm">
                <div>
                  <span className="font-medium text-gray-900">{t.name}</span>
                  {t.is_shared && <span className="ml-2 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">Shared</span>}
                  {t.description && <span className="ml-2 text-xs text-gray-400">{t.description}</span>}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => loadTemplate(t)} className="text-xs text-pme-blue hover:underline">Load</button>
                  <button type="button" onClick={() => void deleteTemplate(t.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
